import WebsiteLanguageSelect from '@formosa/shared/components/WebsiteLanguageSelect';
import { captureAuthError, renderAuthError } from '@formosa/shared/i18n/authErrorState';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import { getInviterCode, INVITER_CODE_KEY } from '@formosa/shared/constants/auth';
import { useTravelText } from '../app/TravelLocaleContext';
import { useAuth } from './AuthContext';
import { authReturnDestination } from './authReturn';
import GoogleSignInButton from './GoogleSignInButton';
import PasswordField from './PasswordField';
import PhoneCountryFields from './PhoneCountryFields';
import { useRegisterConfig } from './useRegisterConfig';
import { useVerifyCodeCooldown } from './useVerifyCodeCooldown';
import './auth.css';

const REGISTER_MODE = { EMAIL: 'email', MOBILE: 'mobile' };
const PASSWORD_PATTERN = /^\S{6,12}$/;

export default function RegisterPage() {
  const { completeAuth } = useAuth();
  const { locale, t, tp } = useTravelText();
  const navigate = useNavigate();
  const location = useLocation();
  const { registerConfig } = useRegisterConfig();
  const destination = authReturnDestination(location.state?.from);
  const cooldown = useVerifyCodeCooldown(60);

  const availableModes = useMemo(() => {
    const modes = [];
    if (registerConfig.emailRegister) modes.push(REGISTER_MODE.EMAIL);
    if (registerConfig.phoneRegister) modes.push(REGISTER_MODE.MOBILE);
    return modes.length > 0 ? modes : [REGISTER_MODE.EMAIL];
  }, [registerConfig.emailRegister, registerConfig.phoneRegister]);

  const [mode, setMode] = useState(REGISTER_MODE.EMAIL);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  // 只保留安全的公開錯誤 key，於 render 時依目前語系翻譯。
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const errorText = renderAuthError(error, t);
  const [form, setForm] = useState({
    username: '',
    password: '',
    nickname: '',
    email: '',
    mobile: '',
    countryKey: '',
    countryName: '',
    phoneAreaCode: '',
    inviterCode: '',
    verifyCode: '',
  });

  useEffect(() => {
    const inviter = getInviterCode();
    if (inviter) {
      localStorage.setItem(INVITER_CODE_KEY, inviter);
      setForm((prev) => (prev.inviterCode ? prev : { ...prev, inviterCode: inviter }));
    }
  }, []);

  useEffect(() => {
    if (!availableModes.includes(mode)) setMode(availableModes[0]);
  }, [availableModes, mode]);

  function updateField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function updatePhoneCountry(patch) {
    setForm((prev) => ({
      ...prev,
      countryKey: patch.countryKey ?? prev.countryKey,
      countryName: patch.countryEnName ?? prev.countryName,
      phoneAreaCode: patch.phoneAreaCode ?? prev.phoneAreaCode,
    }));
  }

  const canSubmit = useMemo(() => {
    if (!agreed) return false;
    if (!form.username.trim()) return false;
    if (!PASSWORD_PATTERN.test(form.password)) return false;
    if (!form.verifyCode.trim()) return false;
    if (registerConfig.needPromotionCode && !form.inviterCode.trim()) return false;
    if (mode === REGISTER_MODE.EMAIL) return Boolean(form.email.trim());
    return Boolean(form.mobile.trim() && form.countryName && form.phoneAreaCode);
  }, [agreed, form, mode, registerConfig.needPromotionCode]);

  async function sendVerifyCode() {
    setError(null);
    setSuccess('');
    setSendingCode(true);
    try {
      if (mode === REGISTER_MODE.EMAIL) {
        if (!form.email.trim()) {
          setError({ key: `travel.auth.register.emailRequired` });
          return;
        }
        await memberApi.sendVerifyCodeEmail({ scene: 'REGISTER', email: form.email.trim() });
      } else {
        if (!form.mobile.trim()) {
          setError({ key: `travel.auth.register.mobileRequired` });
          return;
        }
        await memberApi.sendVerifyCodePhone({
          scene: 'REGISTER',
          countryEnName: form.countryName,
          phoneAreaCode: form.phoneAreaCode,
          phoneNumber: form.mobile.trim(),
        });
      }
      setSuccess(mode === REGISTER_MODE.EMAIL ? 'travel.auth.emailVerificationAccepted' : 'travel.auth.register.codeSent');
      cooldown.start();
    } catch (err) {
      setError(captureAuthError(err, `travel.auth.register.codeFailed`));
    } finally {
      setSendingCode(false);
    }
  }

  async function submit(event) {
    event.preventDefault();
    if (!canSubmit || submitting) {
      if (!agreed) setError({ key: `travel.auth.register.agreeRequired` });
      return;
    }
    setError(null);
    setSuccess('');
    setSubmitting(true);
    try {
      const payload = {
        username: form.username.trim(),
        password: form.password,
        nickname: form.nickname.trim() || undefined,
        inviterCode: form.inviterCode.trim() || undefined,
        verifyCode: form.verifyCode.trim(),
        countryName: form.countryName || undefined,
      };
      const data =
        mode === REGISTER_MODE.EMAIL
          ? await memberApi.registerEmail({ ...payload, email: form.email.trim() })
          : await memberApi.registerMobile({ ...payload, mobile: form.mobile.trim() });
      completeAuth(data);
      navigate(destination, { replace: true });
    } catch (err) {
      setError(captureAuthError(err, `travel.auth.register.failed`));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="travel-auth">
      <form className="travel-auth__card" onSubmit={submit}>
        <Link className="travel-auth__brand" to="/explore">Formosa Travel</Link>
        <WebsiteLanguageSelect locale={locale} t={t} />
        <h1>{t(`travel.auth.register.title`)}</h1>
        <p>{t(`travel.auth.register.subtitle`)}</p>

        {availableModes.length > 1 ? (
          <div className="travel-auth__tabs" role="tablist" aria-label={t(`travel.auth.register.methodLabel`)}>
            {availableModes.includes(REGISTER_MODE.EMAIL) ? (
              <button type="button" role="tab" aria-selected={mode === REGISTER_MODE.EMAIL}
                className={mode === REGISTER_MODE.EMAIL ? 'is-active' : ''}
                onClick={() => setMode(REGISTER_MODE.EMAIL)}>{t(`travel.auth.email`)}</button>
            ) : null}
            {availableModes.includes(REGISTER_MODE.MOBILE) ? (
              <button type="button" role="tab" aria-selected={mode === REGISTER_MODE.MOBILE}
                className={mode === REGISTER_MODE.MOBILE ? 'is-active' : ''}
                onClick={() => setMode(REGISTER_MODE.MOBILE)}>{t(`travel.auth.mobile`)}</button>
            ) : null}
          </div>
        ) : null}

        {errorText ? <div className="travel-auth__error" role="alert">{errorText}</div> : null}
        {success ? <div className="travel-auth__success" role="status">{t(success)}</div> : null}

        <label className="travel-auth__field">
          <span>{t(`travel.auth.register.username`)}</span>
          <input required value={form.username} onChange={(e) => updateField('username', e.target.value)}
            autoComplete="username" disabled={submitting} />
        </label>

        <PasswordField
          label={t(`travel.auth.register.password`)}
          required
          minLength={6}
          maxLength={12}
          autoComplete="new-password"
          value={form.password}
          onChange={(e) => updateField('password', e.target.value)}
          disabled={submitting}
        />

        <label className="travel-auth__field">
          <span>{t(`travel.auth.register.nickname`)}</span>
          <input value={form.nickname} onChange={(e) => updateField('nickname', e.target.value)}
            disabled={submitting} />
        </label>

        {mode === REGISTER_MODE.EMAIL ? (
          <label className="travel-auth__field">
            <span>{t(`travel.auth.email`)}</span>
            <input required type="email" value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              autoComplete="email" disabled={submitting} />
          </label>
        ) : (
          <>
            <PhoneCountryFields
              idPrefix="register-phone"
              countryKey={form.countryKey}
              countryEnName={form.countryName}
              phoneAreaCode={form.phoneAreaCode}
              onChange={updatePhoneCountry}
              allowedEnNames={registerConfig.enNames}
              disabled={submitting || sendingCode}
            />
            <label className="travel-auth__field">
              <span>{t(`travel.auth.mobile`)}</span>
              <input required type="tel" value={form.mobile}
                onChange={(e) => updateField('mobile', e.target.value)}
                autoComplete="tel" disabled={submitting} />
            </label>
          </>
        )}

        <label className="travel-auth__field">
          <span>{t(registerConfig.needPromotionCode ? `travel.auth.register.inviterRequired` : `travel.auth.register.inviterOptional`)}</span>
          <input value={form.inviterCode}
            onChange={(e) => updateField('inviterCode', e.target.value)}
            required={registerConfig.needPromotionCode}
            disabled={submitting} />
        </label>

        <div className="travel-auth__code-block">
          <label className="travel-auth__field" htmlFor="travel-register-verify">
            <span>{t(`travel.auth.register.verifyCode`)}</span>
            <input
              id="travel-register-verify"
              required
              value={form.verifyCode}
              onChange={(e) => updateField('verifyCode', e.target.value)}
              disabled={submitting}
            />
          </label>
          <button
            type="button"
            className="travel-auth__secondary"
            onClick={sendVerifyCode}
            disabled={sendingCode || cooldown.active || submitting}
          >
            {sendingCode
              ? t(`travel.auth.register.sendingCode`)
              : cooldown.active
                ? tp(`travel.auth.register.resendIn`, cooldown.remaining)
                : t(`travel.auth.register.sendCode`)}
          </button>
        </div>

        <label className="travel-auth__check travel-auth__check--block">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={submitting}
          />
          <span>{t(`travel.auth.register.agree`)}</span>
        </label>
        {/* 條款連結獨立於勾選標籤，不以連結拼接句子；目的地不變 */}
        <nav className="travel-auth__legal-links" aria-label={t(`travel.auth.register.legalLinksLabel`)}>
          <Link to="/agreement">{t(`travel.auth.register.terms`)}</Link>
          <Link to="/privacy-policy">{t(`travel.auth.register.privacy`)}</Link>
        </nav>

        <button type="submit" className="travel-auth__primary" disabled={submitting}>
          {t(submitting ? `travel.auth.register.submitting` : `travel.auth.register.submit`)}
        </button>

        <div className="travel-auth__divider"><span>{t(`travel.auth.or`)}</span></div>
        <GoogleSignInButton onError={(message) => setError(message ? { key: 'authErrors.generic' } : null)} mode="register" />

        <p className="travel-auth__footer">
          <span>{t(`travel.auth.register.haveAccount`)}</span>
          {' '}
          <Link to="/login" state={location.state}>{t(`travel.auth.register.loginLink`)}</Link>
        </p>
      </form>
    </main>
  );
}
